import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Los filtros de una tabla, iguales en todas las pantallas.
 *
 * Existe porque cada tabla resolvía lo suyo: Usuarios tenía una casilla suelta
 * para las bajas, Documentos una hilera de listas desplegables, y Grupos,
 * Correos y Lecturas no tenían nada. Tres formas distintas de filtrar en el
 * mismo sistema son tres cosas que aprender.
 */
export function BarraDeFiltros({ children }: { children: ReactNode }) {
  return <div className="barra-filtros">{children}</div>;
}

export function Buscador({
  etiqueta,
  valor,
  onCambio,
  id = 'buscador',
}: {
  etiqueta: string;
  valor: string;
  onCambio: (valor: string) => void;
  id?: string;
}) {
  return (
    <div className="campo buscador">
      <label htmlFor={id}>{etiqueta}</label>
      <span className="buscador__icono" aria-hidden="true">
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6">
          <circle cx="7" cy="7" r="4.5" />
          <path d="M10.5 10.5 14 14" strokeLinecap="round" />
        </svg>
      </span>
      <input
        id={id}
        type="search"
        value={valor}
        placeholder={etiqueta}
        onChange={(e) => onCambio(e.target.value)}
      />
    </div>
  );
}

export interface OpcionSegmentada {
  valor: string;
  etiqueta: string;
  /** Cuántos hay. Un filtro que lleva a una tabla vacía se evita antes de pulsarlo. */
  conteo?: number | undefined;
}

/**
 * Un filtro de pocas opciones excluyentes, todas a la vista.
 *
 * A diferencia de una lista desplegable, se ve de un vistazo qué hay y cuántos:
 * «Bajas (3)» dice que existen tres antes de pulsar, y «Bajas (0)» evita el
 * viaje a una tabla vacía.
 */
export function Segmentado({
  etiqueta,
  opciones,
  valor,
  onCambio,
}: {
  etiqueta: string;
  opciones: OpcionSegmentada[];
  valor: string;
  onCambio: (valor: string) => void;
}) {
  return (
    <div className="campo">
      <span className="campo__etiqueta-filtro">{etiqueta}</span>
      <div className="segmentado" role="group" aria-label={etiqueta}>
        {opciones.map((opcion) => (
          <button
            key={opcion.valor}
            type="button"
            className={`segmentado__opcion${opcion.valor === valor ? ' segmentado__opcion--activa' : ''}`}
            aria-pressed={opcion.valor === valor}
            onClick={() => onCambio(opcion.valor)}
          >
            {opcion.etiqueta}
            {opcion.conteo !== undefined && (
              <span className="segmentado__conteo">{opcion.conteo}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Quita todos los filtros. Sólo aparece cuando hay alguno puesto. */
export function LimpiarFiltros({ hay, onLimpiar }: { hay: boolean; onLimpiar: () => void }) {
  const { t } = useTranslation();
  if (!hay) return null;
  return (
    <button type="button" className="boton boton--fantasma filtros__limpiar" onClick={onLimpiar}>
      {t('common.clearFilters')}
    </button>
  );
}
